import React from "react";

interface Props {
  children?: React.ReactNode;
}

type ThemeType = "dark" | "light";

interface ThemeContextType {
  themeType: ThemeType;
  /** Apply a route-specific theme override (e.g. onboarding hero pages). Not persisted. */
  setTheme: (e: ThemeType) => void;
  /** Drop any route-specific override and restore the light default. */
  clearThemeOverride: () => void;
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => React.useContext(ThemeContext);

const applyDocumentTheme = (theme: ThemeType): void => {
  const root = document.documentElement;
  if (theme === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
};

/**
 * Lightweight theme context — toggles `data-theme` on the document root for the
 * design-system token layer. MUI ThemeProvider removed (REG-378 T037).
 * Dark mode is disabled for user selection (REG-496): the app always boots in
 * light mode. `setTheme` remains for transient, route-driven overrides only.
 */
export const ThemeProviderContainer: React.FC<Props> = ({ children }) => {
  const [override, setOverride] = React.useState<ThemeType | null>(null);
  const themeType = override ?? "light";

  const setTheme = React.useCallback((e: ThemeType) => {
    setOverride(e);
  }, []);

  const clearThemeOverride = React.useCallback(() => {
    setOverride(null);
  }, []);

  React.useLayoutEffect(() => {
    applyDocumentTheme(themeType);
  }, [themeType]);

  return <ThemeContext.Provider value={{ themeType, setTheme, clearThemeOverride }}>{children}</ThemeContext.Provider>;
};
